/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import LoadingIndicator from '../LoadingIndicator'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import TableSortLabel from '@mui/material/TableSortLabel'
import { descendingComparator, getComparator } from '../../../constants/constants'
import { getHoursMinutes } from '../../../utils/baseStringUtils'
import { type IChartTableRowProps, type IChartTableHeaderProps, type IChartTableProps } from '../../../interfaces/chart-table.interface'
import { type ISleepData } from '../../../interfaces/sleep-summary.interface'
import { mapProfileDispatch } from '../../../constants/reduxMappers'

function SleepHeader (props: IChartTableHeaderProps): JSX.Element {
  const order = props.order

  const createSortHandler = (property) => (event): void => {
    props.onRequestSort(event, property)
  }

  const renderHeader = (): JSX.Element => {
    return (
      <TableHead>
        <TableRow>
          <TableCell key='date'>
            <TableSortLabel active direction={order} onClick={createSortHandler('date')}>
              Date
            </TableSortLabel>
          </TableCell>

          <TableCell align='center' className='odd-col'>Start</TableCell>
          <TableCell align='center'>End</TableCell>
          <TableCell align='center' className='odd-col'>Disruptions</TableCell>
          <TableCell align='center'>Duration</TableCell>
        </TableRow>
      </TableHead>
    )
  }

  return renderHeader()
}

interface ISleepRowProps extends IChartTableRowProps {
  data: {
    measurements: ISleepData []
  }
}
function SleepRow (props: ISleepRowProps): JSX.Element {
  const dateAsMoment = moment(props.date)
  const formattedDate = moment().isSame(dateAsMoment, 'year') ? dateAsMoment.format('MMM DD') : dateAsMoment.format('MMM DD, YYYY')

  // sort the measurements in asc or desc based on date sort
  props.data.measurements.sort((a, b) => props.order === 'desc' ? descendingComparator(a.date, b.date) : -descendingComparator(a.date, b.date))

  const renderRow = (): JSX.Element => {
    return (
      <>
        {props.data?.measurements.map(measurement => {
          const duration = getHoursMinutes(measurement.duration.value)

          return (
            <TableRow sx={{ backgroundColor: theme => `${theme.palette.background.paper}!important` }} key={measurement.date}>
              <TableCell> <Typography display='flex'> {formattedDate} </Typography> </TableCell>
              <TableCell align='center' className='odd-col'>{moment(measurement.start).format('h:mm a')}</TableCell>
              <TableCell align='center'>{moment(measurement.end).format('h:mm a')}</TableCell>
              <TableCell align='center' className='odd-col'>{measurement.wake_up_count}</TableCell>
              <TableCell align='center'>{duration.hours} hr {duration.minutes} min</TableCell>
            </TableRow>
          )
        })}
      </>
    )
  }

  return renderRow()
}

type IGroupedData = Record<string | number, {
  measurements: ISleepData []
}>

function SleepTable (props: IChartTableProps): JSX.Element {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const handleRequestSort = (): void => { setOrder(order === 'asc' ? 'desc' : 'asc') }

  const groupByDate = (data: ISleepData []): IGroupedData => {
    const groupedData = {}

    data.forEach((d) => {
      if (!groupedData[d.date]) groupedData[d.date] = { measurements: [] }
      const newItem = {
        ...d
      }
      groupedData[d.date].measurements.unshift(newItem)
    })
    return groupedData
  }

  const renderTable = (): JSX.Element => {
    if (props.dashboardViewStore.isRangeLoading) {
      return (<div className='metrics-section__inner'> <LoadingIndicator /> </div>)
    }

    const dataSection = props.memberObj.sleep
    if (!dataSection?.data?.length) {
      return (
        <Box>
          <Typography variant='body1' color='#7f7f7f' marginTop={1.5}>No sleep data for this period</Typography>
        </Box>
      )
    }

    const dataByDate = groupByDate(dataSection.data)
    const sectionClass = !props.hideBorder ? 'metrics-section' : ''
    return (
      <TableContainer className={sectionClass} sx={{ height: 408 }}>
        {!props.hideHeader && <h3 className='metrics-section__header'>Sleep</h3>}
        <Table className='blood-pressure-table__container' stickyHeader>
          <SleepHeader order={order} onRequestSort={handleRequestSort} />
          <TableBody>
            {Object.keys(dataByDate).slice().sort(getComparator(order)).map((date, i) => {
              return <SleepRow order={order} date={date} data={dataByDate[date]} key={i} actions={props.profileViewActions} />
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return renderTable()
}

const connector = connect(null, mapProfileDispatch)
export default connector(SleepTable)
